import React from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover, PopoverContent } from 'react-bootstrap';
import { Input } from '../Input/Input';

/*
  this component will be used to render warning icon and message
  parameters:
    errorMessage: string - error message to be displayed
*/
const Warning = ({ errorMessage }) => (
  <OverlayTrigger
    placement="top"
    trigger={['hover', 'focus']}
    overlay={
      <Popover style={{ minWidth: 'auto' }}>
        <PopoverContent>
          <p>{errorMessage}</p>
        </PopoverContent>
      </Popover>
    }
  >
    <p className="bp-warning-paragraph">
      <span className="warning bp-warning"></span>
    </p>
  </OverlayTrigger>
);

/*
  this component will be used to render dropdown input fields
  parameters:
    value: string - value of the dropdown
    onChange: function - function to be called when dropdown value changes
    options: object - options to be displayed in the dropdown
    isEditing: boolean - flag to check if the dropdown is in editing mode
*/
const DropdownInput = ({ value, onChange, options, isEditing, hideDropDownDefaultSelectOption, disabled }) => {
  return (
    <select
      className={`select-element ${disabled && isEditing && 'bp-select-disabled-input'}`}
      value={value === null ? '' : value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!isEditing || disabled}
    >
      {!hideDropDownDefaultSelectOption && <option value="">-- Select --</option>}
      {Object.entries(options).map(([key, value]) => (
        <option key={key} value={key}>
          {value}
        </option>
      ))}
    </select>
  );
};

// this component will be used to render text input fields
const TextInput = ({ value, onChange }) => (
  <input type="text" defaultValue={value} className="input-element" onChange={(e) => onChange(e.target.value)} />
);

const DateInput = ({ value, onChange }) => (
  <Input name="date" type="date" hideLabel noBorders onChange={(e) => onChange(e.target.value)} />
);

// Integer input - only allows integers, can be null/empty
const IntegerInput = ({ value, onChange, disabled }) => (
  <input
    type="text"
    inputMode="numeric"
    className="input-element"
    value={value === null || value === '' ? '' : String(value)}
    onChange={(e) => {
      const v = e.target.value.trim();
      if (v === '') onChange(null);
      else if (/^-?\d+$/.test(v)) onChange(v);
    }}
    disabled={disabled}
  />
);

// Fee with unit: number input (2 decimals) + dropdown (£/%) on same row
const FeeWithUnitInput = ({
  value,
  unit,
  unitOptions,
  onChangeValue,
  onChangeUnit,
  isEditing,
  hasError,
  errorMessage,
}) => {
  const hasFeeValue = value != null && value !== '';
  const dropdownDisabled = !isEditing || !hasFeeValue;

  const handleValueChange = (e) => {
    const v = e.target.value.trim();
    if (v === '') {
      onChangeValue(null);
      onChangeUnit(null);
    } else if (/^\d*\.?\d{0,2}$/.test(v)) {
      onChangeValue(v);
    }
  };

  return (
    <div className={`bp-fee-with-unit ${hasError ? 'bp-grid-error-cell' : ''}`}>
      <input
        type="text"
        inputMode="decimal"
        className="input-element"
        value={value === null || value === '' ? '' : String(value)}
        onChange={handleValueChange}
        disabled={!isEditing}
      />
      <select
        className="select-element"
        value={unit === null || unit === '' ? '' : unit}
        onChange={(e) => onChangeUnit(e.target.value || null)}
        disabled={dropdownDisabled}
      >
        <option value="">-- Select --</option>
        {Object.entries(unitOptions || {}).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {hasError && <Warning errorMessage={errorMessage} />}
    </div>
  );
};

const NotesButton = ({ onClick, linkText }) => (
  <button className="button-text-link view-notes-button" onClick={onClick}>
    {linkText}
  </button>
);

const CheckboxGroup = ({ value, disabled, isEditing, subTitle, onChange }) => {
  return (
    <div className="bp-grid-checkboxes fancy-checkbox">
      <span class="checkbox">
        <input
          type="checkbox"
          id={subTitle}
          name={subTitle}
          disabled={!isEditing || disabled}
          onChange={(e) => onChange(e.target.checked ? '1' : '0')}
          checked={value !== null && value !== '0'}
        />
        <svg>
          <use xlinkHref="#checkbox" class="checkbox"></use>
        </svg>
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
        <symbol id="checkbox" viewBox="0 0 22 22">
          <path
            fill="none"
            stroke="currentColor"
            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
          />
        </symbol>
      </svg>
    </div>
  );
};

// Element Renderer
const RenderElement = ({
  title,
  subTitle,
  value,
  isEditing,
  itemType,
  options,
  onChange,
  hasError,
  errorMessage,
  setModalShow,
  hideDropDownDefaultSelectOption,
  unit,
  unitOptions,
  onUnitChange,
  disabled,
}) => {
  if (itemType === 'integer') {
    return (
      <div className={`${hasError && 'bp-grid-error-cell'}`}>
        {isEditing ? (
          <IntegerInput value={value} onChange={onChange} disabled={!isEditing || disabled} />
        ) : (
          <p className="bpgrid-value">{value != null && value !== '' ? value : ''}</p>
        )}
        {hasError && <Warning errorMessage={errorMessage} />}
      </div>
    );
  }

  if (itemType === 'feeWithUnit') {
    return (
      <>
        {isEditing ? (
          <FeeWithUnitInput
            value={value}
            unit={unit}
            unitOptions={unitOptions}
            onChangeValue={onChange}
            onChangeUnit={onUnitChange}
            isEditing={isEditing}
            hasError={hasError}
            errorMessage={errorMessage}
          />
        ) : (
          <div className={`${hasError && 'bp-grid-error-cell'}`}>
            <p className="bpgrid-value">
              {value != null && value !== ''
                ? `${value} ${unit && unitOptions?.[unit] ? unitOptions[unit] : ''}`.trim()
                : ''}
            </p>
            {hasError && <Warning errorMessage={errorMessage} />}
          </div>
        )}
      </>
    );
  }

  if (itemType === 'dropdown') {
    return (
      <div className={`${hasError && 'bp-grid-error-cell'}`}>
        <p className="bp-select">
          <DropdownInput
            value={value}
            onChange={onChange}
            options={options}
            isEditing={isEditing}
            title={title}
            hideDropDownDefaultSelectOption={hideDropDownDefaultSelectOption}
            disabled={disabled}
          />
        </p>
        {hasError && <Warning errorMessage={errorMessage} />}
      </div>
    );
  }

  if (itemType === 'notes') {
    return (
      <>
        <NotesButton onClick={setModalShow} linkText={'View Notes'} /> (Notes: {value || 0})
      </>
    );
  }

  if (itemType === 'documentsList') {
    return <NotesButton onClick={setModalShow} linkText={'View'} />;
  }

  //if itemType is checkbox, return CheckboxGroup. All the logic for checkboxes will be handled in CheckboxGroup
  if (itemType === 'checkbox') {
    return (
      <CheckboxGroup value={value} options={options} isEditing={isEditing} subTitle={subTitle} onChange={onChange} />
    );
  }

  if (isEditing) {
    switch (itemType) {
      case 'dropdown':
        return (
          <DropdownInput
            value={value}
            onChange={onChange}
            options={options}
            isEditing={isEditing}
            title={title}
            hideDropDownDefaultSelectOption={hideDropDownDefaultSelectOption}
          />
        );
      case 'date':
        return <DateInput value={value} onChange={onChange} />;
      case 'string':
        return <TextInput value={value} onChange={onChange} />;
      default:
        return null;
    }
  }

  if (itemType === 'dropdown') {
    return (
      <div className={`${hasError && 'bp-grid-error-cell'}`}>
        <p className="bpgrid-value">{options[value]}</p>
        {hasError && <Warning errorMessage={errorMessage} />}
      </div>
    );
  }

  if (itemType === 'date') {
    if (!value) return null;

    const dateParts = value.split('-');
    const dateDisplayValue = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
    return (
      <div>
        <p className="bpgrid-value">{dateDisplayValue}</p>
      </div>
    );
  }

  return (
    <div className={`${hasError && 'bp-grid-error-cell'}`}>
      <p className="bpgrid-value">{value}</p>
      {hasError && <Warning errorMessage={errorMessage} />}
    </div>
  );
};

// Main Grid Component
//========================================================
const BPGrid = ({ children, numCols, style, className }) => (
  <div
    className={`bp-table-grid bp-grid-default ${className}`}
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))`,
      ...style,
    }}
  >
    {React.Children.map(children, (child) => React.cloneElement(child))}
  </div>
);

// Sub-components
//========================================================
BPGrid.Header = function Header({ children }) {
  return <div className="bp-table-grid-header bp-grid-default">{children}</div>;
};

BPGrid.Row = function Row({
  title,
  subTitle,
  alternateSubtitle,
  tooltip,
  value,
  isEditingValue,
  valueType,
  dropdownValues,
  onChange,
  hasError,
  hasHeaderError,
  errorMessage,
  setModalShow,
  tableTitle,
  className,
  hideDropDownDefaultSelectOption,
  unit,
  onUnitChange,
  unitOptions,
  disabled,
}) {
  return (
    <>
      {tooltip ? (
        <OverlayTrigger
          placement="top"
          trigger={['hover', 'focus']}
          overlay={
            <Popover style={{ minWidth: 'auto' }}>
              <PopoverContent>
                <p>{tooltip}</p>
              </PopoverContent>
            </Popover>
          }
        >
          <div className={`bp-table-grid-header bp-grid-default ${hasHeaderError && 'bp-table-error'}`}>
            {alternateSubtitle || subTitle}
          </div>
        </OverlayTrigger>
      ) : (
        <div className={`bp-table-grid-header bp-grid-default ${hasHeaderError && 'bp-table-error'}`}>
          {alternateSubtitle || subTitle}
        </div>
      )}

      <div className={`bp-table-grid-cell bp-grid-default ${className}`}>
        <RenderElement
          title={title}
          subTitle={subTitle}
          value={value}
          isEditing={isEditingValue}
          itemType={valueType}
          options={dropdownValues}
          onChange={(newValue) => onChange(subTitle, newValue, tableTitle)}
          hasError={hasError}
          errorMessage={errorMessage}
          setModalShow={setModalShow}
          hideDropDownDefaultSelectOption={hideDropDownDefaultSelectOption}
          unit={unit}
          onUnitChange={onUnitChange ? (v) => onUnitChange(subTitle, v, tableTitle) : undefined}
          unitOptions={unitOptions}
          disabled={disabled}
        />
      </div>
    </>
  );
};

// PropTypes and DefaultProps
//========================================================
Warning.propTypes = {
  errorMessage: PropTypes.string.isRequired,
};

DropdownInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.objectOf(PropTypes.string).isRequired,
  isEditing: PropTypes.bool.isRequired,
  hideDropDownDefaultSelectOption: PropTypes.bool,
  disabled: PropTypes.bool,
};

TextInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

DateInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

IntegerInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

FeeWithUnitInput.propTypes = {
  value: PropTypes.string.isRequired,
  unit: PropTypes.string,
  unitOptions: PropTypes.objectOf(PropTypes.string),
  onChangeValue: PropTypes.func.isRequired,
  onChangeUnit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

NotesButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  linkText: PropTypes.string.isRequired,
};

CheckboxGroup.propTypes = {
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isEditing: PropTypes.bool.isRequired,
  subTitle: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

RenderElement.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
  isEditing: PropTypes.bool.isRequired,
  itemType: PropTypes.string.isRequired,
  options: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  setModalShow: PropTypes.func,
  hideDropDownDefaultSelectOption: PropTypes.bool,
  unit: PropTypes.string,
  unitOptions: PropTypes.objectOf(PropTypes.string),
  onUnitChange: PropTypes.func,
  disabled: PropTypes.bool,
};

RenderElement.defaultProps = {
  title: '',
  value: '',
  options: {},
  hasError: false,
  errorMessage: '',
  setModalShow: () => {},
  hideDropDownDefaultSelectOption: false,
  unit: '',
  unitOptions: {},
  onUnitChange: undefined,
  disabled: false,
};

BPGrid.propTypes = {
  children: PropTypes.node.isRequired,
  numCols: PropTypes.number.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
};

BPGrid.defaultProps = {
  style: {},
  className: '',
};

BPGrid.Header.propTypes = {
  children: PropTypes.node.isRequired,
};

BPGrid.Row.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
  isEditingValue: PropTypes.bool,
  valueType: PropTypes.string,
  dropdownValues: PropTypes.object,
  onChange: PropTypes.func,
  tableTitle: PropTypes.string,
  hasError: PropTypes.bool,
  hasHeaderError: PropTypes.bool,
  errorMessage: PropTypes.string,
  setModalShow: PropTypes.func,
  className: PropTypes.string,
  hideDropDownDefaultSelectOption: PropTypes.bool,
  unit: PropTypes.string,
  onUnitChange: PropTypes.func,
  unitOptions: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
};

BPGrid.Row.defaultProps = {
  title: '',
  value: '',
  isEditingValue: false,
  valueType: 'string',
  dropdownValues: {},
  onChange: () => {},
  tableTitle: '',
  hasError: false,
  hasHeaderError: false,
  errorMessage: '',
  setModalShow: () => {},
  className: '',
  hideDropDownDefaultSelectOption: false,
  unit: '',
  onUnitChange: undefined,
  unitOptions: {},
  disabled: false,
};

export default BPGrid;
